import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFloating, offset, useDismiss, useRole, useClick, useInteractions, shift, arrow, } from "@floating-ui/react";
import { ThemeProviderContext } from "../ThemeProvider";
import { Button } from "../Button";
import classNames from "classnames";
import { LinkOutIcon } from "../icons";
import { Tooltip } from "../Tooltip";
import { useEffect, useRef, useState } from "react";
import { useLinkUrl } from "./useLinkUrl";
import { CustomPopover } from "./CustomPopover";
export const useEditLinkPopover = ({ editor, onClickEditLink, }) => {
    const arrowRef = useRef(null);
    const [showEditLinkPopover, setShowEditLinkPopover] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const { refs, context, strategy, middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }, x, y, } = useFloating({
        strategy: "fixed",
        open: true,
        placement: "top",
        middleware: [offset(10), shift(), arrow({ element: arrowRef })],
    });
    const { getFloatingProps } = useInteractions([
        useClick(context, { enabled: true }),
        useRole(context),
        useDismiss(context, { enabled: true }),
    ]);
    const setEditLinkPopoverPosition = (boundingClientRect, boundingClientRange) => {
        refs.setReference({
            getBoundingClientRect: () => boundingClientRect,
            getClientRects: () => boundingClientRange,
        });
    };
    const isLinkNode = () => {
        if (!editor)
            return;
        const pos = editor.view.state.selection.$anchor.pos;
        const node = editor.view.state.doc.nodeAt(pos);
        if (!node)
            return;
        const linkNode = node.marks.find((mark) => {
            if (!mark)
                return false;
            if (mark.type.name === "link" && "href" in mark.attrs)
                return true;
            else {
                return false;
            }
        });
        return linkNode;
    };
    const onEditorClickCallback = (e) => {
        if (!editor)
            return;
        const selection = window.getSelection();
        if (!(selection === null || selection === void 0 ? void 0 : selection.isCollapsed)) {
            hideEditLinkPopover();
            return;
        }
        const clickNode = e.target;
        const linkNode = isLinkNode();
        if (linkNode && clickNode.tagName === "A") {
            setEditLinkPopoverPosition(clickNode.getBoundingClientRect(), clickNode.getClientRects());
            setLinkUrl(linkNode.attrs.href);
            setShowEditLinkPopover(true);
        }
        else {
            setShowEditLinkPopover(false);
        }
    };
    const unsetLink = () => {
        if (!editor)
            return;
        const pos = editor.view.state.selection.$anchor.pos;
        const linkNode = editor.view.state.doc.nodeAt(pos);
        if (linkNode && linkNode.text) {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            setShowEditLinkPopover(false);
        }
    };
    const editLink = () => {
        if (!editor)
            return;
        const pos = editor.view.state.selection.$anchor.pos;
        const linkNode = editor.view.state.doc.nodeAt(pos);
        if (linkNode && linkNode.text) {
            const existingLink = editor.isActive("link")
                ? editor.getAttributes("link")
                : null;
            setShowEditLinkPopover(false);
            onClickEditLink({
                text: linkNode.text,
                href: existingLink && existingLink.href,
            });
        }
    };
    const hideEditLinkPopover = () => {
        setShowEditLinkPopover(false);
    };
    useEffect(() => {
        editor === null || editor === void 0 ? void 0 : editor.on("update", () => {
            if (showEditLinkPopover)
                hideEditLinkPopover();
        });
    }, [editor, showEditLinkPopover]);
    return {
        EditLinkPopover,
        editLinkPopoverProps: {
            context,
            getFloatingProps,
            refs,
            strategy,
            y,
            x,
            arrowRef,
            arrowX,
            arrowY,
            linkUrl,
            unsetLink,
            editLink,
        },
        showEditLinkPopover,
        setShowEditLinkPopover,
        setEditLinkPopoverPosition,
        onEditorClickCallback,
        hideEditLinkPopover,
    };
};
const Separator = () => (_jsx("div", { className: "border-r h-3 border-border-regular-darkmode" }));
export const EditLinkPopover = ({ context, getFloatingProps, refs, strategy, y, x, arrowRef, linkUrl, arrowX, arrowY, unsetLink, editLink, }) => {
    const { truncatedUrl, httpsUrl } = useLinkUrl(linkUrl);
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ menuZIndex, translate }) => (_jsx(CustomPopover, { menuZIndex: menuZIndex, context: context, refs: refs, getFloatingProps: getFloatingProps, strategy: strategy, x: x, y: y, arrowRef: arrowRef, arrowX: arrowX, arrowY: arrowY, id: "edit-link-tooltip", children: _jsxs("div", { className: "flex", children: [_jsxs("a", { target: "_blank", href: httpsUrl, rel: "noreferrer", className: "flex items-center", children: [_jsx(LinkOutIcon, { className: "hover:!text-text-brand w-[12px] h-[12px]" }), _jsx("span", { className: "pl-0.5 pr-1", children: truncatedUrl })] }), _jsx(Separator, {}), _jsxs("div", { className: "flex items-center", children: [_jsx(IconButton, { tooltipContent: translate("components.rich_text_editor.edit_link_popover.unlink"), iconName: "Unlink", onClick: unsetLink, ariaLabel: "unlink" }), _jsx(Separator, {}), _jsx(IconButton, { tooltipContent: translate("components.rich_text_editor.edit_link_popover.edit"), iconName: "Pencil", ariaLabel: "Pencil", onClick: editLink, className: "!pr-0.5" })] })] }) })) }));
};
const IconButton = ({ iconName, ariaLabel, tooltipContent, onClick, className, }) => {
    return (_jsx(Tooltip, { content: tooltipContent, triggerAction: "hover", trigger: _jsx(Button, { size: "small", iconName: iconName, variant: "tertiary-neutral", className: classNames("w-2 !px-1.5 !rounded-none hover:!bg-transparent hover:!text-text-brand [&>svg]:w-[12px] [&>svg]:h-[12px]", className), onClick: onClick, ariaLabel: ariaLabel, backgroundStyle: "transparent" }), placement: "top", openDelay: 300 }));
};
//# sourceMappingURL=useEditLinkPopover.js.map